/**
 * B2 · CLASSROOM CHAT — local-first mutations.
 *
 * They write to PowerSync and go up through the write-path (`db/sync.rs`),
 * which is what RULES: it derives `organization_id` from the cohort, requires
 * ACTIVE enrollment, applies the rate limit, the edit window, and the closed
 * emoji catalog, and only accepts hide/pin/promote from whoever moderates.
 * What's here is the optimistic view, never the authorization.
 *
 * The ids of `cohort_chat_read` and `cohort_chat_reaction` are DETERMINISTIC
 * and generated by the client. It's not a whim: both tables have a natural
 * `UNIQUE`, so with random ids two devices of the same person would write two
 * rows and the second one would be lost against the index. The server checks
 * the format and discards anything that doesn't match.
 */

import {
  and,
  cohort_chat_message,
  cohort_chat_reaction,
  cohort_chat_read,
  eq,
  type PowerSyncDrizzleDB,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";

/**
 * Allowed emojis. EXACT mirror of `CHAT_ALLOWED_EMOJI`
 * (`core-rust/src/use_cases/classroom.rs`) and of the CHECK in migration 00092:
 * the server discards anything else, so offering more here would only produce
 * reactions that vanish on sync.
 */
export const CHAT_REACTION_EMOJIS = ["👍", "🎉", "❤️", "😮", "🤔", "✅"] as const;
export type ChatReactionEmoji = (typeof CHAT_REACTION_EMOJIS)[number];

/** Character cap (== `cohort_chat_body_len`, migration 00091). */
export const CHAT_MAX_BODY_CHARS = 4000;

/** Minutes during which the author can correct their own message. */
export const CHAT_EDIT_WINDOW_MINUTES = 5;

/** Deterministic id of MY read marker in a classroom. */
export function chatReadId(cohortId: string, userId: string): string {
  return `chatread:${cohortId}:${userId}`;
}

/** Deterministic id of MY reaction to a message. */
export function chatReactionId(messageId: string, userId: string): string {
  return `chatreact:${messageId}:${userId}`;
}

/** Sends a message to a cohort's chat. Returns its id. */
export async function sendChatMessageMutation(
  db: PowerSyncDrizzleDB,
  params: { cohortId: string; organizationId?: string | null; authorId: string; body: string },
): Promise<string> {
  const now = nowISO();
  const id = generateId();
  await db.insert(cohort_chat_message).values({
    id,
    cohort_id: params.cohortId,
    // Optimistic: the server REWRITES it with the cohort's. Filled in only so
    // the local row is coherent until the first checkpoint.
    organization_id: params.organizationId ?? null,
    author_id: params.authorId,
    body: params.body.trim().slice(0, CHAT_MAX_BODY_CHARS),
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * `cohortId`: the classroom the caller ALREADY had open (a required parameter
 * of `useChatActions`, not extra data to resolve) — it's this domain's REAL
 * authorization boundary: the write-path requires "ACTIVE enrollment in
 * THIS cohort" to write/moderate (see the file header), not membership in
 * an organization. `organization_id` also exists in the table, but it would
 * be a laxer AND less precise `scope` — two cohorts of the SAME org are
 * mutually foreign for this chat, so `cohort_id` is the only one that
 * reflects that boundary. See the 2026-08-13 audit (aula-courses-app
 * measured the same kind of error by using the shape of the `WHERE` instead
 * of the real boundary).
 */

/** Corrects one's own message (the server requires authorship and window). */
export async function editChatMessageMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  body: string,
  cohortId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort_chat_message,
    cohort_chat_message.id,
    messageId,
    { body: body.trim().slice(0, CHAT_MAX_BODY_CHARS) },
    withScope(options, [{ column: cohort_chat_message.cohort_id, value: cohortId }]),
  );
}

/** Deletes one's own message (or anyone's, if the requester moderates). `cohortId`: see note above. */
export async function deleteChatMessageMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  cohortId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort_chat_message,
    cohort_chat_message.id,
    messageId,
    { deleted_at: nowISO() },
    withScope(options, [{ column: cohort_chat_message.cohort_id, value: cohortId }]),
  );
}

/**
 * Hides or restores a message. Only the server decides whether the requester
 * moderates; hiding TAKES the message OUT of the student's replica, it doesn't
 * just hide it. `cohortId`: see note in `editChatMessageMutation`.
 */
export async function setChatMessageHiddenMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  hidden: boolean,
  cohortId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort_chat_message,
    cohort_chat_message.id,
    messageId,
    { hidden_at: hidden ? nowISO() : null },
    withScope(options, [{ column: cohort_chat_message.cohort_id, value: cohortId }]),
  );
}

/** Pins or unpins a message (exempts it from the retention window). `cohortId`: see note in `editChatMessageMutation`. */
export async function setChatMessagePinnedMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  pinned: boolean,
  cohortId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort_chat_message,
    cohort_chat_message.id,
    messageId,
    { pinned_at: pinned ? nowISO() : null },
    withScope(options, [{ column: cohort_chat_message.cohort_id, value: cohortId }]),
  );
}

/**
 * Links a message to the forum thread it was promoted to. The server checks
 * that the thread belongs to THIS cohort: without that, the chip and its link
 * would point to a foreign forum. `cohortId`: see note in `editChatMessageMutation`.
 */
export async function linkChatMessageToThreadMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  threadId: string,
  cohortId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort_chat_message,
    cohort_chat_message.id,
    messageId,
    { promoted_thread_id: threadId },
    withScope(options, [{ column: cohort_chat_message.cohort_id, value: cohortId }]),
  );
}

/**
 * Updates MY read marker for the classroom. Idempotent via the deterministic
 * id; the server also only lets the marker ADVANCE (an operation queued
 * offline doesn't resurrect unread items already read on another device).
 *
 * ⚠️ Class 2 (POWERSYNC_VERIFIED_WRITE.md §3): the SELECT by `chatReadId(...)`
 * decides INSERT-vs-UPDATE within the window. Unlike
 * `submitAssignmentMutation`, the id IS deterministic (not random), so a
 * false "doesn't exist" doesn't duplicate a DIFFERENT row — it retries an
 * INSERT with the SAME id as the real row, and collides against the PK when
 * that row finally syncs (same mechanism as `ThemeProvider`'s `insert(user)`,
 * which DOES catch "UNIQUE constraint failed"; this INSERT doesn't catch it).
 * Not solved with a blind `waitForRowPresent`: the deterministic id is also
 * that of a person's FIRST read marker in a new classroom, and waiting ~6s of
 * backoff there would be a UX regression for the normal path.
 * Documented and characterized with a test; not hardened.
 */
export async function markChatReadMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId?: string | null;
    userId: string;
    at?: string;
    mutedUntil?: string | null;
  },
): Promise<void> {
  const now = nowISO();
  const id = chatReadId(params.cohortId, params.userId);
  const existing = await db
    .select({ id: cohort_chat_read.id })
    .from(cohort_chat_read)
    .where(eq(cohort_chat_read.id, id))
    .limit(1);
  const values = {
    last_read_at: params.at ?? now,
    ...(params.mutedUntil === undefined ? {} : { muted_until: params.mutedUntil }),
    updated_at: now,
  };
  if (existing.length > 0) {
    await db.update(cohort_chat_read).set(values).where(eq(cohort_chat_read.id, id));
    return;
  }
  await db.insert(cohort_chat_read).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    user_id: params.userId,
    created_at: now,
    ...values,
  });
}

/** Mutes (or unmutes) notifications for THIS classroom. */
export async function setChatMutedMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId?: string | null;
    userId: string;
    mutedUntil: string | null;
  },
): Promise<void> {
  await markChatReadMutation(db, { ...params, at: undefined });
}

/**
 * Adds or changes MY reaction to a message (one per message and person).
 *
 * ⚠️ Same Class 2 as `markChatReadMutation` (see its comment): deterministic
 * id (`chatReactionId`), SELECT-decides within the window.
 * Documented and characterized, not hardened with a blind wait.
 */
export async function reactToChatMessageMutation(
  db: PowerSyncDrizzleDB,
  params: {
    messageId: string;
    cohortId: string;
    organizationId?: string | null;
    userId: string;
    emoji: ChatReactionEmoji;
  },
): Promise<void> {
  const id = chatReactionId(params.messageId, params.userId);
  const existing = await db
    .select({ id: cohort_chat_reaction.id })
    .from(cohort_chat_reaction)
    .where(eq(cohort_chat_reaction.id, id))
    .limit(1);
  if (existing.length > 0) {
    await db
      .update(cohort_chat_reaction)
      .set({ emoji: params.emoji })
      .where(eq(cohort_chat_reaction.id, id));
    return;
  }
  await db.insert(cohort_chat_reaction).values({
    id,
    message_id: params.messageId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    user_id: params.userId,
    emoji: params.emoji,
    created_at: nowISO(),
  });
}

/** Removes MY reaction (real deletion: a removed reaction leaves no trace). */
export async function removeChatReactionMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  userId: string,
): Promise<void> {
  await db
    .delete(cohort_chat_reaction)
    .where(
      and(
        eq(cohort_chat_reaction.id, chatReactionId(messageId, userId)),
        eq(cohort_chat_reaction.user_id, userId),
      ),
    );
}
